import React from 'react';
import HelpTooltip from '../HelpTooltip';
import OeTip from './OeTip';
import {fmt, fmt0} from './format';
import {eur} from './freight';

// Frachtvergleich-Panel (Original `freightPanel` 2638–2681 und
// `wireFreightPanel` 2681).
//
// Es rechnet den Molotow-Kostenrechner nach: Ziel, Gewicht, Volumen,
// bestätigbare Palettenzahl und die drei Wege in echten Euro. Die
// Palettenzahlen sind Eingaben, weil nur das Lager weiß, wie die Ware
// tatsächlich gestapelt wird — die Vorgabe ist ein grober Startwert.
//
// Neu gegenüber dem Original: Dieselbe Rechnung läuft im Server
// (`FreightCalculator`). Weicht sie um mehr als einen Cent ab, steht das hier
// als Hinweis — zwei Rechenwege sind nur dann eine Kontrolle, wenn eine
// Abweichung auch auffällt (ABGLEICH N30).

const WEG_LABEL = {lahr: 'Alles ab Lahr', snv: 'Alles ab SNV', split: '2 Direktlieferungen'};

/** Zahleneingabe der Palettenfelder — ganze Zahlen, nie negativ. */
function PalletInput({label, value, onChange, ariaLabel, tip, disabled}) {
    return (
        <OeTip tip={tip}>
            <input
                className="fb-in"
                inputMode="numeric"
                autoComplete="off"
                aria-label={ariaLabel || label}
                value={value}
                disabled={disabled}
                onChange={(e) => {
                    const roh = e.target.value.replace(/[^\d]/g, '');
                    onChange(roh === '' ? null : Math.max(0, parseInt(roh, 10)));
                }}
            />
        </OeTip>
    );
}

/**
 * @param {object} fc - Ergebnis aus `freightComparison`.
 * @param {object} shipTo - Lieferziel aus `/stock`.
 * @param {{total: ?number, Lahr: ?number, SNV: ?number}} palletInput
 * @param {(key: string, value: ?number) => void} onPallet
 * @param {?boolean} sprayOverride
 * @param {(value: ?boolean) => void} onSpray
 * @param {?{ways: object, cheapest: ?string}} serverAbweichung - Abweichung der Serverrechnung.
 * @param {boolean} canEdit
 */
export default function FreightPanel({
    fc,
    shipTo,
    palletInput,
    onPallet,
    sprayOverride,
    onSpray,
    serverAbweichung,
    canEdit,
}) {
    if (!fc) {
        return null;
    }

    if (!fc.supported) {
        const land = (shipTo && (shipTo.city || shipTo.countryCode))
            ? `${shipTo.city || ''} ${shipTo.countryCode || ''}`.trim()
            : 'Zielland';
        return (
            <div className="frachtbox">
                <div className="fb-h">
                    🚚 Frachtvergleich
                    <HelpTooltip id="oe-fb-none" placement="bottom" size={0.7}>
                        Die Frachttarife werden einmal im Jahr aus dem Molotow-Kostenrechner übernommen. Für
                        Länder ohne Tarif rechnet der Editor nicht — dort bleibt es bei der Auskunft aus dem
                        Einkauf.
                    </HelpTooltip>
                </div>
                <div className="muted">
                    Für <b>{land}</b> ist kein Tarif in der Excel hinterlegt — bitte manuell / bei KS prüfen.
                </div>
            </div>
        );
    }

    const sw = fc.sw;
    const to = shipTo ? `${shipTo.zip || ''} ${shipTo.city || ''} · ${shipTo.countryCode || ''}` : '';
    const pi = palletInput || {};

    const fehler = (r) => (r && r.err ? <span className="fb-err">{r.err}</span> : null);

    const zeile = (titel, betrag, rechts, fehlerBlock) => (
        <tr>
            <td>{titel}</td>
            <td className="num"><b>{eur(betrag)}</b>{fehlerBlock}</td>
            <td className="muted">{rechts}</td>
        </tr>
    );

    return (
        <div className="frachtbox">
            <div className="fb-h">
                🚚 Frachtvergleich <span className="muted">(nach Molotow-Kostenrechner)</span>
                <HelpTooltip
                    id="oe-fb-help"
                    placement="bottom"
                    size={0.7}
                    detail={<>
                        Gerechnet wird nach dem Molotow-Kostenrechner: Grundfracht nach PLZ-Zone und Frachtgewicht,
                        dazu Dieselfloater, Maut, Paletten-, Großstadt-, Spraydosen- und Ausfuhrzuschläge.
                    </>}
                >
                    Rechnet die Frachtkosten der drei Wege. Nur eine Entscheidungshilfe, keine Buchung — es wird
                    nichts gespeichert.
                </HelpTooltip>
            </div>

            <div className="fb-grid">
                <div>
                    <span className="muted">Ziel</span><br/>
                    <b>{to}</b>
                </div>
                <div>
                    <span className="muted">Gewicht</span><br/>
                    <b>{fmt0(sw.total)} kg</b>
                    {fc.mixed ? (
                        <span className="muted"> (Lahr {fmt0(sw.Lahr)} · SNV {fmt0(sw.SNV)})</span>
                    ) : null}
                </div>
                <div>
                    <span className="muted">Volumen</span><br/>
                    ~{fmt(sw.vol)} m³
                    {sw.missing ? <span className="muted"> ({sw.missing} o. Maße)</span> : null}
                </div>
                <div>
                    <span className="muted">Paletten gesamt</span><br/>
                    <PalletInput
                        label="Paletten gesamt"
                        ariaLabel="Paletten gesamt"
                        tip={'Anzahl Paletten der Gesamtsendung. Sie geht mit 25 kg je Palette ins Frachtgewicht '
                            + 'ein und bestimmt den Palettenzuschlag. Die Vorgabe ist nur eine Schätzung — bitte '
                            + 'bestätigen. Wird nicht gespeichert.'}
                        value={pi.total != null ? String(pi.total) : String(fc.pTotal)}
                        disabled={!canEdit}
                        onChange={(v) => onPallet('total', v)}
                    />
                    {' '}<span className="muted">Default {fc.dTotal}</span>
                </div>
                <div className="fb-spray">
                    <OeTip tip={'Spraydosen lösen einen Gefahrgut-Zuschlag aus (Schenker 10 €, DHL 35 €). '
                        + 'Vorbelegt aus dem VOC-Wert der Artikel — hier übersteuerbar, wenn es im Einzelfall '
                        + 'anders ist. Wird nicht gespeichert.'}>
                        <button
                            type="button"
                            className={`chkbtn${fc.spray ? ' on' : ''}`}
                            role="switch"
                            aria-checked={fc.spray}
                            disabled={!canEdit}
                            onClick={() => onSpray(!fc.spray)}
                        >
                            {fc.spray ? '✓' : '○'} Spraydosen enthalten
                        </button>
                    </OeTip>
                    {sprayOverride !== null && sprayOverride !== undefined ? (
                        <OeTip tip="Setzt die Angabe auf den Wert aus den Artikeldaten (VOC) zurück.">
                            <button type="button" className="rc-link" onClick={() => onSpray(null)}>↺ automatisch</button>
                        </OeTip>
                    ) : null}
                </div>
            </div>

            {fc.mixed ? (
                <div className="fb-split">
                    <span className="muted">Split-Paletten:</span>
                    <span>Lahr</span>
                    <PalletInput
                        label="Split-Paletten Lahr"
                        ariaLabel="Split-Paletten Lahr"
                        tip="Paletten der Lahr-Sendung, wenn getrennt direkt zum Kunden geliefert wird."
                        value={pi.Lahr != null ? String(pi.Lahr) : String(fc.pLahr)}
                        disabled={!canEdit}
                        onChange={(v) => onPallet('Lahr', v)}
                    />
                    <span>({fmt0(sw.Lahr)} kg)</span>
                    <span>SNV</span>
                    <PalletInput
                        label="Split-Paletten SNV"
                        ariaLabel="Split-Paletten SNV"
                        tip="Paletten der Schneider-Sendung, wenn getrennt direkt zum Kunden geliefert wird."
                        value={pi.SNV != null ? String(pi.SNV) : String(fc.pSNV)}
                        disabled={!canEdit}
                        onChange={(v) => onPallet('SNV', v)}
                    />
                    <span>({fmt0(sw.SNV)} kg)</span>
                </div>
            ) : null}

            <table className="fb-tab">
                <tbody>
                    {zeile(
                        'Alles ab Lahr',
                        fc.lahr.total,
                        <>1 Sendung{fc.lahr.fw ? ` · ${fmt0(fc.lahr.fw)} kg Frachtgew.` : ''}</>,
                        fehler(fc.lahr),
                    )}
                    {zeile(
                        'Alles ab SNV',
                        fc.snv.total,
                        <>1 Sendung{fc.snv.fw ? ` · ${fmt0(fc.snv.fw)} kg Frachtgew.` : ''}</>,
                        fehler(fc.snv),
                    )}
                    {fc.mixed ? zeile(
                        '2 Direktlieferungen',
                        fc.split.total,
                        <>
                            Lahr {eur(fc.split.l.total)}{fehler(fc.split.l)}
                            {' + '}SNV {eur(fc.split.s.total)}{fehler(fc.split.s)}
                        </>,
                        null,
                    ) : null}
                </tbody>
            </table>

            <div className="fb-fazit">
                {fc.mixed && fc.cheapest ? (
                    <>
                        💰 Günstigste Fracht: <b>{WEG_LABEL[fc.cheapest]}</b> ({eur(fc.opts[fc.cheapest])}).
                        {(fc.opts.split != null && fc.cheapest !== 'split')
                            ? ` Zwei Direktlieferungen kosten ${eur(fc.opts.split)} — Differenz `
                                + `${eur(Math.abs(fc.opts.split - fc.opts[fc.cheapest]))}.`
                            : ''}
                        {(fc.cheapest === 'split' && fc.opts.lahr != null)
                            ? ` Eine Sendung ab Lahr kostet ${eur(fc.opts.lahr)}.`
                            : ''}
                    </>
                ) : (
                    <span className="muted">
                        Alle Positionen liegen in <b>{sw.Lahr > 0 ? 'Lahr' : (sw.SNV > 0 ? 'SNV' : 'einem Lager')}</b>
                        {' '}— es gibt keine sinnvolle 2-Lieferungen-Variante (das andere Lager müsste erst
                        beigestellt werden).
                    </span>
                )}
            </div>

            {serverAbweichung ? (
                <OeTip tip={serverAbweichung.tip}>
                    <div className="fb-err" style={{marginTop: 6}}>⚠ Serverrechnung weicht ab</div>
                </OeTip>
            ) : null}

            <div className="muted" style={{fontSize: 11, marginTop: 5}}>
                Frachtgewicht = Ist-Gewicht + 25 kg × Paletten (mit Länder-Mindestgewichten). Paletten bitte
                bestätigen. Beistellung (1 Sendung) hat zusätzlich internen Umlager-Aufwand, der hier nicht
                eingerechnet ist.
            </div>
        </div>
    );
}
